import { MapPin, Monitor, Globe, ExternalLink, Clock, Tag, ChevronDown, ChevronUp } from 'lucide-react';
import { useState } from 'react';

interface VisitorInfoProps {
  contact?: {
    name?: string;
    email?: string;
    metadata?: any;
  };
}

export default function VisitorInfo({ contact }: VisitorInfoProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!contact?.metadata) {
    return null;
  }

  const metadata = contact.metadata;

  // Format location string
  const getLocation = () => {
    const parts: string[] = [];
    if (metadata.city) parts.push(metadata.city);
    if (metadata.region) parts.push(metadata.region);
    if (metadata.country) parts.push(metadata.country);
    return parts.join(', ') || 'Unknown';
  };

  // Format device string
  const getDevice = () => {
    const parts: string[] = [];
    if (metadata.device_type) {
      parts.push(metadata.device_type.charAt(0).toUpperCase() + metadata.device_type.slice(1));
    }
    if (metadata.os) parts.push(metadata.os);
    return parts.join(' • ') || 'Unknown';
  };

  // Format browser string
  const getBrowser = () => {
    if (metadata.browser) {
      return metadata.browser_version
        ? `${metadata.browser} ${metadata.browser_version}`
        : metadata.browser;
    }
    return 'Unknown';
  };

  return (
    <div className="bg-gray-50 border-b border-gray-200">
      {/* Mobile: Collapsible with button */}
      <div className="md:hidden">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full p-3 flex items-center justify-between text-sm font-semibold text-gray-900 hover:bg-gray-100"
        >
          <span>Visitor Context</span>
          {isExpanded ? (
            <ChevronUp className="w-4 h-4 text-gray-600" />
          ) : (
            <ChevronDown className="w-4 h-4 text-gray-600" />
          )}
        </button>

        {isExpanded && (
          <div className="p-3 pt-0 space-y-3">
            {/* Mobile: Vertical layout */}
            {(metadata.city || metadata.country) && (
              <div className="flex items-start gap-2">
                <MapPin className="w-4 h-4 text-gray-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-gray-500">Location</p>
                  <p className="text-sm text-gray-900 truncate">{getLocation()}</p>
                </div>
              </div>
            )}

            {metadata.device_type && (
              <div className="flex items-start gap-2">
                <Monitor className="w-4 h-4 text-gray-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-gray-500">Device</p>
                  <p className="text-sm text-gray-900">{getDevice()}</p>
                </div>
              </div>
            )}

            {metadata.browser && (
              <div className="flex items-start gap-2">
                <Globe className="w-4 h-4 text-gray-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-gray-500">Browser</p>
                  <p className="text-sm text-gray-900">{getBrowser()}</p>
                </div>
              </div>
            )}

            {metadata.page_url && (
              <div className="flex items-start gap-2">
                <ExternalLink className="w-4 h-4 text-gray-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-gray-500">Page</p>
                  <a
                    href={metadata.page_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-black hover:text-gray-900 hover:underline truncate block"
                  >
                    {metadata.page_title || new URL(metadata.page_url).hostname}
                  </a>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Desktop: Horizontal layout, always visible */}
      <div className="hidden md:block p-3">
        <div className="flex items-center gap-6 text-sm flex-wrap">
          {/* Location */}
          {(metadata.city || metadata.country) && (
            <div className="flex items-center gap-1.5">
              <MapPin className="w-4 h-4 text-gray-500 flex-shrink-0" />
              <span className="text-gray-900 font-medium">{getLocation()}</span>
            </div>
          )}

          {/* Device */}
          {metadata.device_type && (
            <div className="flex items-center gap-1.5">
              <Monitor className="w-4 h-4 text-gray-500 flex-shrink-0" />
              <span className="text-gray-900">{getDevice()}</span>
            </div>
          )}

          {/* Browser */}
          {metadata.browser && (
            <div className="flex items-center gap-1.5">
              <Globe className="w-4 h-4 text-gray-500 flex-shrink-0" />
              <span className="text-gray-900">{getBrowser()}</span>
            </div>
          )}

          {/* Page */}
          {metadata.page_url && (
            <div className="flex items-center gap-1.5 max-w-md">
              <ExternalLink className="w-4 h-4 text-gray-500 flex-shrink-0" />
              <a
                href={metadata.page_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-black hover:text-gray-900 hover:underline truncate"
                title={metadata.page_url}
              >
                {metadata.page_title || new URL(metadata.page_url).pathname}
              </a>
            </div>
          )}

          {/* UTM Campaign */}
          {metadata.utm_campaign && (
            <div className="flex items-center gap-1.5">
              <Tag className="w-4 h-4 text-gray-500 flex-shrink-0" />
              <span className="text-gray-900 text-xs bg-gray-100 px-2 py-0.5 rounded">
                {metadata.utm_campaign}
              </span>
            </div>
          )}

          {/* First Seen */}
          {metadata.first_seen && (
            <div className="flex items-center gap-1.5 text-xs text-gray-500 ml-auto">
              <Clock className="w-3.5 h-3.5 flex-shrink-0" />
              <span>First seen: {new Date(metadata.first_seen).toLocaleDateString()}</span>
            </div>
          )}
        </div>

        {/* Second row for referrer and IP if present */}
        {(metadata.referrer || metadata.ip_address || metadata.utm_source) && (
          <div className="flex items-center gap-6 text-xs text-gray-500 mt-2 flex-wrap">
            {metadata.referrer && (
              <span className="truncate" title={metadata.referrer}>
                From: {new URL(metadata.referrer).hostname}
              </span>
            )}
            {metadata.utm_source && (
              <span>
                Source: <span className="font-medium text-gray-700">{metadata.utm_source}</span>
                {metadata.utm_medium && ` / ${metadata.utm_medium}`}
              </span>
            )}
            {metadata.ip_address && (
              <span className="font-mono ml-auto">{metadata.ip_address}</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
